<template>
    <modal @close="onClose" dialog="modal-lg" :disableAutoClose="!!editPayment" :preventClose="isSaving" :class="['reservation-modal payment dark-top pay-in-modal', { 'pay-in-modal--compact': compactModal, 'pay-in-modal--scope': currentStep === STEP.SCOPE }]">
        <div slot="title">
            <h2>Pay In</h2>
        </div>

        <div slot="body">
            <!-- Step progress bar -->
            <div v-if="!editPayment" class="pay-in__steps px-4 pt-3 pb-1">
                <div
                    v-for="(step, index) in STEPS"
                    :key="step.id"
                    class="pay-in__step-group"
                >
                    <div
                        class="pay-in__step"
                        :class="{ 'pay-in__step--active': currentStep === step.id, 'pay-in__step--done': currentStep > step.id }"
                    >
                        <span class="pay-in__step-number">
                            <template v-if="currentStep > step.id">&#10003;</template>
                            <template v-else>{{ step.id }}</template>
                        </span>
                        <span class="pay-in__step-label">{{ step.label }}</span>
                    </div>
                    <span v-if="index < STEPS.length - 1" class="pay-in__step-line" :class="{ 'pay-in__step-line--done': currentStep > step.id }"></span>
                </div>
            </div>

            <step1-scope
                v-if="currentStep === STEP.SCOPE"
                :value="selectedScope"
                :grandTotal="scopeGrandTotal"
                :amountDue="amountDue"
                :selectedReservation="getSelectedReservation"
                @input="selectedScope = $event"
            />

            <step2-details
                v-if="currentStep === STEP.DETAILS && selectedScope === PAYMENT_SCOPE.FULL_BOOKING"
                :form="fullBookingForm"
                :grandTotal="scopeGrandTotal"
                :amountDue="amountDue"
                :showInvoice="!editPayment"
                :editPayment="editPayment"
            />

            <step2-partial-items
                v-if="currentStep === STEP.DETAILS && selectedScope === PAYMENT_SCOPE.PARTIAL_ITEMS"
                ref="step2Partial"
                :form="partialItemsForm"
                :grandTotal="scopeGrandTotal"
                :amountDue="amountDue"
                :createdPayments="partialCreatedPayments"
                :getReservationCreation="getReservationCreation"
                :showPaymentDetailsForm="partialShowPaymentDetailsForm"
                :editPayment="editPayment"
                :showInvoice="!editPayment"
                @validityChanged="partialItemsValid = $event"
                @itemsDirtyChanged="partialItemsDirty = $event"
                @paymentDetailsFormView="compactModal = !$event"
                @update:createdPayments="partialCreatedPayments = $event"
                @update:showPaymentDetailsForm="partialShowPaymentDetailsForm = $event"
            />

            <step3-summary
                v-if="currentStep === STEP.SUMMARY"
                :isFromCreatePayin="true"
                :form="fullBookingForm"
                :selectedScope="selectedScope"
                :partialCreatedPayments="partialCreatedPayments"
                :amountDue="amountDue"
                :invoiceMode.sync="invoiceMode"
            />
        </div>

        <div slot="footer">
            <button type="button" class="btn cancel" :disabled="isSaving" @click="onClose">Cancel</button>
            <button
                v-if="!editPayment && currentStep > STEP.SCOPE"
                type="button"
                class="btn blue ok"
                :disabled="isSaving"
                @click="goBack"
            >
                <i class="fas fa-chevron-left mr-1"></i> Back
            </button>
            <button
                v-if="editPayment"
                type="button"
                class="btn ok green"
                :disabled="isEditSaveDisabled || isSaving"
                @click="save"
            >
                <loader v-if="isSaving" size="small" />
                <template v-else>Save</template>
            </button>
            <button
                v-else
                type="button"
                :class="['btn ok', currentStep === STEP.SUMMARY ? 'green' : 'blue']"
                :disabled="isNextDisabled || isSaving"
                @click="nextStep"
            >
                <loader v-if="isSaving" size="small" />
                <template v-else-if="currentStep === STEP.SUMMARY">Save</template>
                <template v-else>Next <i class="fas fa-chevron-right ml-1"></i></template>
            </button>
        </div>
    </modal>
</template>

<script>
import Vue from 'vue'
import { mapGetters, mapActions } from 'vuex'
import Modal from './Modal'
import Step1Scope from './ReservationPayIn/Step1Scope'
import Step2Details from './ReservationPayIn/Step2Details'
import Step3Summary from './ReservationPayIn/Step3Summary'
import Step2PartialItems from './ReservationPayIn/Step2PartialItems'
import { STEP, STEPS, PAYMENT_SCOPE, buildPaymentLine, buildBillingDetails, CASH_PAYMENT_TYPE_ID, AGENT_DEPOSIT_PAYMENT_TYPE_ID } from './ReservationPayIn/payInHelpers'
import PAY_IN_SCOPE from '@/enums/payInScope'
import payInService from '@/api/reservation/payInService'
import Loader from '@/tools/loaders/loader1'
import { eventHub } from "@/main"

const buildPaymentForm = () => ({
    payments: [],
    notes: '',
    reference: '',
    createInvoice: false,
    addBillingDetails: false,
    billingDetails: buildBillingDetails(),
})

export default {
    components: { Modal, Step1Scope, Step2Details, Step2PartialItems, Step3Summary, Loader },
    props: {
        editPayment: {
            type: Object,
            default: null,
        },
    },
    created() {
        if (!this.editPayment) return
        this.currentStep = STEP.DETAILS
        if (this.editPayment.isFullAmount === false) {
            this.selectedScope = PAYMENT_SCOPE.PARTIAL_ITEMS
            this.partialItemsForm.notes = this.editPayment.description || ''
            this.partialItemsForm.reference = this.editPayment.reference || ''
            this.prefillBillingDetails(this.partialItemsForm)
            this.original = {
                reference: this.partialItemsForm.reference || '',
                notes: this.partialItemsForm.notes || '',
                billingSig: this.billingSignature(this.partialItemsForm),
            }
            return
        }
        this.selectedScope = PAYMENT_SCOPE.FULL_BOOKING
        this.fullBookingForm.payments = (this.editPayment.allocations || []).map(a => ({
            type: a.type,
            toPay: a.amount - (a.change || 0),
            amountReceived: a.amount,
            change: a.change || 0,
            allocationId: a.id,
            canBeDeleted: a.canBeDeleted,
            isAgentPayment: !!a.isAgentPayment,
        }))
        this.fullBookingForm.notes = this.editPayment.description || ''
        this.prefillBillingDetails(this.fullBookingForm)
        this.original = {
            notes: this.fullBookingForm.notes || '',
            billingSig: this.billingSignature(this.fullBookingForm),
            paymentsSig: this.paymentsSignature(this.fullBookingForm.payments),
        }
    },
    data() {
        return {
            STEP,
            STEPS,
            PAYMENT_SCOPE,
            currentStep: STEP.SCOPE,
            selectedScope: null,
            invoiceMode: null,
            fullBookingForm: buildPaymentForm(),
            partialItemsForm: buildPaymentForm(),
            partialItemsValid: false,
            compactModal: false,
            partialCreatedPayments: [],
            partialShowPaymentDetailsForm: true,
            isSaving: false,
            original: {},
            partialItemsDirty: false,
        }
    },
    computed: {
        ...mapGetters([
            'getReservationCalculations',
            'getSelectedReservation',
            'getReservationCreation',
            'getCountries'
        ]),
        amountDue() {
            if (this.editPayment) return this.editPayment.value || 0
            return this.getReservationCalculations.totalInDue || 0
        },
        scopeGrandTotal() {
            if (this.editPayment) return this.editPayment.value || 0
            if (this.getReservationCreation) return this.getReservationCalculations.grandTotal
            return this.getSelectedReservation && this.getSelectedReservation.billingInDue != null
                ? this.getSelectedReservation.billingInDue
                : this.getReservationCalculations.grandTotal
        },
        totalReceived() {
            return this.fullBookingForm.payments.reduce((sum, payment) => sum + (parseFloat(payment.amountReceived) || 0), 0)
        },
        isAmountFullyPaid() {
            return this.totalReceived >= this.amountDue && this.amountDue > 0
        },
        isSomethingPaid() {
            return this.totalReceived > 0
        },
        isFullBookingBillingValid() {
            if (!this.fullBookingForm.addBillingDetails) return true
            return !!this.fullBookingForm.billingDetails.vat && !!this.fullBookingForm.billingDetails.country
        },
        isNextDisabled() {
            if (this.currentStep === STEP.SCOPE) return !this.selectedScope
            if (this.currentStep === STEP.DETAILS && this.selectedScope === PAYMENT_SCOPE.FULL_BOOKING) {
                const hasEmptyRows = this.fullBookingForm.payments.some(p => !p.amountReceived)
                const hasZeroToPay = this.fullBookingForm.payments.some(p => !(parseFloat(p.toPay) > 0))
                const paymentValidation = !this.editPayment && !this.isSomethingPaid
                return !this.isFullBookingBillingValid || hasEmptyRows || hasZeroToPay || paymentValidation
            }
            if (this.currentStep === STEP.DETAILS && this.selectedScope === PAYMENT_SCOPE.PARTIAL_ITEMS) return !this.partialItemsValid
            return false
        },
        isOnPartialItemsWithFormOpen() {
            return this.currentStep === STEP.DETAILS
                && this.selectedScope === PAYMENT_SCOPE.PARTIAL_ITEMS
                && this.partialCreatedPayments.length > 0
                && this.partialShowPaymentDetailsForm
        },
        hasChanges() {
            if (!this.editPayment) return true
            if (this.selectedScope === PAYMENT_SCOPE.PARTIAL_ITEMS) {
                const metaChanged = (this.partialItemsForm.reference || '') !== this.original.reference
                    || (this.partialItemsForm.notes || '') !== this.original.notes
                    || this.billingSignature(this.partialItemsForm) !== this.original.billingSig
                return metaChanged || this.partialItemsDirty
            }
            return (this.fullBookingForm.notes || '') !== this.original.notes
                || this.billingSignature(this.fullBookingForm) !== this.original.billingSig
                || this.paymentsSignature(this.fullBookingForm.payments) !== this.original.paymentsSig
        },
        isEditSaveDisabled() {
            return this.isNextDisabled || !this.hasChanges
        },
    },
    watch: {
        currentStep(step) {
            if (step !== STEP.DETAILS) {
                this.compactModal = false
                return
            }
            if (this.selectedScope === PAYMENT_SCOPE.PARTIAL_ITEMS) {
                this.compactModal = !this.partialShowPaymentDetailsForm
            }
            if (!this.editPayment && this.selectedScope === PAYMENT_SCOPE.FULL_BOOKING && !this.fullBookingForm.payments.length) {
                this.fullBookingForm.payments = [buildPaymentLine(null)]
            }
        },
    },
    beforeDestroy() {
        this.setIsReservationCreationPayInPartialFullAmount(false);
    },
    methods: {
        ...mapActions(['closeModal', 'refreshCurrentSelectedReservation','addFloatingNotification', 'setIsReservationCreationPayInPartialFullAmount']),
        onClose() {
            if (!this.editPayment) this.closeModal()
            this.$emit('close')
        },
        nextStep() {
            if (this.currentStep === STEP.SUMMARY) {
                this.save()
                return
            }
            if(this.currentStep === STEP.DETAILS){
                this.invoiceMode = null;
            }
            if(this.currentStep === 1 && this.selectedScope === PAYMENT_SCOPE.PARTIAL_ITEMS){
                this.setIsReservationCreationPayInPartialFullAmount(false);
            } else if(this.currentStep === 1 && this.selectedScope === PAYMENT_SCOPE.FULL_BOOKING){
                this.setIsReservationCreationPayInPartialFullAmount(true);
            }
            this.currentStep++
        },
        async save() {
            if (this.isSaving) return
            this.isSaving = true
            let reservationResult = null
            try {
                // Reservation must be created first
                if (this.getReservationCreation) {
                    reservationResult = await this.createReservationBeforePayIn()

                    if (!reservationResult || !reservationResult.ok) {
                        this.isSaving = false
                        return
                    }
                }

                const result = this.editPayment
                    ? await payInService.editPayIn(this.buildEditPayload())
                    : await payInService.createPayIn(this.buildPayload(reservationResult ? reservationResult : null))

                if (!result || !result.ok) {
                    const response = result && result.error && result.error.response
                    const beMessage = response && response.data && response.data.error
                    const alreadyToasted = response && response.status === 400 && beMessage
                    Vue.toasted.error(beMessage || 'Something went wrong. Please try again.')
                    return
                }

                if(this.getReservationCreation){
                    this.$toasted.success('Reservation created successfully');
                    this.$router.push({ name: 'reservations' });
                    Vue.toasted.success(
                        this.editPayment
                            ? 'Payment successfully updated.'
                            : 'Payment successfully saved.'
                    )
                    this.addFloatingNotification(
                        {
                            type: -1,
                            title: 'Ticket '+this.formatTicketNumber(reservationResult.bookingId),
                            message: reservationResult.reservation.customer.name+' <br>'+reservationResult.reservation.event.product.name,
                            bookingId: reservationResult.bookingId,
                        },
                        'info',
                        {
                            link: '/reservations/view/'+reservationResult.bookingId
                        }
                    )
                }
                
                if(!this.getReservationCreation){
                    await this.refreshCurrentSelectedReservation()
                }

                this.editPayment ? this.$emit('close') : this.closeModal()
            } catch (e) {
                console.error(e)
                Vue.toasted.error('Something went wrong. Please try again.')
            }finally{
                this.isSaving = false
            }
        },
        createReservationBeforePayIn() {
            return new Promise((resolve) => {
                eventHub.$emit('createReservationBeforePayIn', resolve)
            })
        },
        buildEditPayload() {
            if (this.selectedScope === PAYMENT_SCOPE.PARTIAL_ITEMS) {
                return this.buildPartialEditPayload()
            }
            return {
                paymentId: this.editPayment.id,
                methods: this.fullBookingForm.payments.map((payment) => {
                    const received = parseFloat(payment.amountReceived) || 0
                    const toPay = parseFloat(payment.toPay) || 0
                    return {
                        id: payment.allocationId || 0,
                        method: payment.type,
                        received,
                        change: Math.max(0, received - toPay),
                        isAgentPayment: payment.type === AGENT_DEPOSIT_PAYMENT_TYPE_ID || !!payment.isAgentPayment,
                    }
                }),
                billingDetails: this.fullBookingForm.addBillingDetails
                    ? this.mapBillingDetails(this.fullBookingForm.billingDetails)
                    : null,
                notes: this.fullBookingForm.notes,
                createInvoice: this.fullBookingForm.createInvoice,
            }
        },
        buildPartialEditPayload() {
            const payment = this.$refs.step2Partial.collectPayload()
            const itemsTotal = payment.items.reduce((sum, item) => sum + item.total, 0)
            const change = Math.max(0, payment.total - itemsTotal)
            const cashIndex = payment.paymentMethods.findIndex(paymentMethod => paymentMethod.typeId === CASH_PAYMENT_TYPE_ID)

            return {
                paymentId: this.editPayment.id,
                tickets: payment.paxSnapshot.filter(pax => pax.qtyToAdd > 0).map(pax => ({ id: pax.id, quantity: pax.qtyToAdd, grossPrice: pax.grossprice })),
                components: payment.extrasSnapshot.filter(extra => extra.qtyToAdd > 0).map(extra => ({ id: extra.id, quantity: extra.qtyToAdd })),
                methods: payment.paymentMethods.map((paymentMethod, index) => ({
                    method: paymentMethod.typeId,
                    received: parseFloat(paymentMethod.amountReceived) || 0,
                    change: index === cashIndex ? change : 0,
                    id: paymentMethod.id || 0,
                    isAgentPayment: paymentMethod.typeId === AGENT_DEPOSIT_PAYMENT_TYPE_ID || !!paymentMethod.isAgentPayment,
                })),
                billingDetails: payment.billingDetails ? this.mapBillingDetails(payment.billingDetails) : null,
                createInvoice: payment.createInvoice,
                notes: payment.notes || '',
                reference: payment.reference || '',
            }
        },
        buildPayload(bookingCreationResult) {
            const bookingId = this.getReservationCreation ? bookingCreationResult.bookingId : this.getSelectedReservation.id
            if (this.selectedScope === PAYMENT_SCOPE.FULL_BOOKING) {
                return {
                    bookingId,
                    scope: PAY_IN_SCOPE.FULL,
                    payments: [this.buildFullBookingPayment()],
                    invoiceCreationMode: this.invoiceMode,
                }
            }
            return {
                bookingId,
                scope: PAY_IN_SCOPE.ITEMS,
                payments: this.partialCreatedPayments.map(createdPayment => this.buildPartialPayment(createdPayment,bookingCreationResult)),
                invoiceCreationMode: this.invoiceMode,
            }
        },
        buildFullBookingPayment() {
            return {
                tickets: [],
                components: [],
                methods: this.fullBookingForm.payments.map((paymentLine) => {
                    const received = parseFloat(paymentLine.amountReceived) || 0
                    const toPay = parseFloat(paymentLine.toPay) || 0
                    return {
                        method: paymentLine.type,
                        received,
                        change: Math.max(0, received - toPay),
                        isAgentPayment: paymentLine.type === AGENT_DEPOSIT_PAYMENT_TYPE_ID || !!paymentLine.isAgentPayment,
                    }
                }),
                billingDetails: this.fullBookingForm.addBillingDetails ? this.mapBillingDetails(this.fullBookingForm.billingDetails) : null,
                createInvoice: this.fullBookingForm.createInvoice,
                notes: this.fullBookingForm.notes,
            }
        },
        buildPartialPayment(payment, bookingCreationResult) {
            const itemsTotal = payment.items.reduce((sum, item) => sum + item.total, 0)
            const change = Math.max(0, payment.total - itemsTotal)
            const cashIndex = payment.paymentMethods.findIndex(paymentMethod => paymentMethod.typeId === CASH_PAYMENT_TYPE_ID)
            const reservation = this.getReservationCreation ? bookingCreationResult.reservation : this.getSelectedReservation
            let components = payment.extrasSnapshot
                .filter(extra => extra.qtyToAdd > 0)
                .map(extra => {
                    const component = { quantity: extra.qtyToAdd, grossPrice: extra.componentPrice.discountedGrossprice || extra.componentPrice.grossprice || 0 }
                    
                    if (reservation && reservation.bookingComponents) {
                        // During creation, extra.id is a synthetic composite key (component
                        // id + price id) so extras sharing a component id but with different
                        // prices stay distinct. Match the server booking component on that
                        // same (component, price) identity instead of the composite id.
                        const matchingComponent = reservation.bookingComponents.find(
                            bookingComponent => this.getReservationCreation
                                ? bookingComponent.component && extra.component &&
                                  bookingComponent.component.id === extra.component.id &&
                                  bookingComponent.componentPrice && extra.componentPrice &&
                                  bookingComponent.componentPrice.id === extra.componentPrice.id
                                : bookingComponent.id === extra.id
                        )
                        if (matchingComponent && matchingComponent.hashedValue) {
                            component.hash = matchingComponent.hashedValue
                        }
                    }
                    
                    return component
                })

            let tickets = payment.paxSnapshot
                .filter(pax => pax.qtyToAdd > 0)
                .map(pax => {
                    const ticket = { quantity: pax.qtyToAdd, grossPrice: pax.grossprice }
                    
                    if (reservation && reservation.bookingGenderPrices) {
                        const matchingPax = reservation.bookingGenderPrices.find(
                            genderPrice => genderPrice.id === pax.id
                        )
                        if (matchingPax && matchingPax.hashedAudience) {
                            ticket.hash = matchingPax.hashedAudience
                        }
                    }
                    
                    return ticket
                })
            
            return {
                tickets: tickets,
                components: components,
                methods: payment.paymentMethods.map((paymentMethod, index) => ({
                    method: paymentMethod.typeId,
                    received: parseFloat(paymentMethod.amountReceived) || 0,
                    change: index === cashIndex ? change : 0,
                    isAgentPayment: paymentMethod.typeId === AGENT_DEPOSIT_PAYMENT_TYPE_ID || !!paymentMethod.isAgentPayment,
                })),
                billingDetails: payment.billingDetails ? this.mapBillingDetails(payment.billingDetails) : null,
                createInvoice: payment.createInvoice,
                notes: payment.notes || '',
                reference: payment.reference || '',
            }
        },
        prefillBillingDetails(form) {
            if (!this.editPayment.hasBillingDetails) return
            const details = (this.editPayment.billing && this.editPayment.billing.details) || {}
            const countryId = details.country != null ? details.country : null
            form.addBillingDetails = true
            form.billingDetails = {
                vat: details.vat || '',
                businessName: details.businessName || '',
                address: details.address || '',
                city: details.city || '',
                postalCode: details.postalCode || '',
                country: countryId,
                selectedCountry: this.getCountries.find(country => country.id === countryId) || null,
            }
        },
        billingSignature(form) {
            if (!form.addBillingDetails) return 'none'
            const billingDetails = form.billingDetails || {}
            return JSON.stringify({
                vat: billingDetails.vat || '',
                businessName: billingDetails.businessName || '',
                address: billingDetails.address || '',
                city: billingDetails.city || '',
                postalCode: billingDetails.postalCode || '',
                country: billingDetails.country != null ? billingDetails.country : null,
            })
        },
        paymentsSignature(payments) {
            return JSON.stringify((payments || []).map(payment => ({
                type: payment.type,
                amountReceived: payment.amountReceived,
                toPay: payment.toPay,
            })))
        },
        mapBillingDetails(billingDetails) {
            return {
                vat: billingDetails.vat || '',
                city: billingDetails.city || '',
                zipCode: billingDetails.postalCode || '',
                country: billingDetails.country !== undefined ? billingDetails.country : null,
                name: billingDetails.businessName || '',
                address: billingDetails.address || '',
            }
        },
        goBack() {
            if (this.isOnPartialItemsWithFormOpen) {
                this.partialShowPaymentDetailsForm = false
                return
            }
            this.currentStep--
            if (this.currentStep === STEP.SCOPE) {
                this.fullBookingForm = buildPaymentForm()
                this.partialItemsForm = buildPaymentForm()
                this.partialItemsValid = false
                this.partialCreatedPayments = []
                this.partialShowPaymentDetailsForm = true
            }
        },
    },
}
</script>

<style lang="scss">
    @import '../../assets/scss/_variables.scss';

    .pay-in-modal .pay-in__checkbox-label {
        color: $darkest;
        font-size: $font-size-small;
    }

    .pay-in-modal {
        align-items: flex-start;
        padding-top: 3rem;
    }

    .pay-in-modal .modal-dialog {
        width: 100%;
        max-width: 820px;
        transition: max-width 0.3s ease;
    }

    .pay-in-modal--compact .modal-dialog {
        max-width: 520px;
    }

    .pay-in-modal--scope .modal-dialog {
        max-width: 650px;
    }
</style>

<style lang="scss" scoped>
    @import '../../assets/scss/_variables.scss';

    .pay-in__steps {
        display: flex;
        align-items: flex-start;
        justify-content: center;
        margin-top: 0.75rem;
        padding-bottom: 0.25rem;
    }

    .pay-in__step-group {
        display: contents;
    }

    .pay-in__step {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        color: $dark;
        font-size: 0.85rem;
        white-space: nowrap;
    }

    .pay-in__step--active {
        color: $darkest;
        font-weight: 600;
    }

    .pay-in__step--active,
    .pay-in__step--done {
        .pay-in__step-number {
            background: $color-3;
            border-color: $color-3;
            color: $lightest;
        }
    }

    .pay-in__step-number {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
        width: 1.8rem;
        height: 1.8rem;
        border-radius: 50%;
        border: 1px solid $dark;
        font-size: 0.85rem;
        font-weight: 600;
        flex-shrink: 0;
    }

    .pay-in__step-line {
        flex: 1;
        height: 1px;
        background: $dark;
        min-width: 2.5rem;
        margin: 0.9rem 0.5rem 0;
        opacity: 0.4;

        &--done {
            background: $color-3;
            opacity: 1;
        }
    }
</style>



// WEBPACK FOOTER //
// ReservationPayIn.vue?f47cdb7c